/** Import modules */

import { useTranslation }   from "react-i18next";

import { useLocation, useParams } from "react-router-dom";

import { useRef, useState, useEffect } from "react";

import { Helmet }           from "react-helmet-async";

import {
    hreflangLinks,
    localizedAbsoluteUrl,
    localizedValue,
    normalizeLanguage
}                           from "../../../Components/SEO/localizedMeta";

import { getRobotsDirective } from "../../../Configs/LanguageConfig";

import { buildCategoryPath } from "../../../Misc/Permalinks";

/** Import models */

import Collection           from "../../../Models/RimsCollection";

/** Import hooks */

import Display              from "../../../Hooks/ProductDisplay";

import usePosition          from "../../../Hooks/usePosition";

/** Import components */

import Breadcrumbs          from "../../../Components/Layout/Header/Breadcrumbs";

import ComponentLoader      from "../../../Components/Layout/Loaders/Loader";

import RimsLayout           from "../../../Components/Products/Rims/Catalog/Layout";

import Actions              from "../../../Components/Products/Rims/Catalog/Actions";

import Products             from "../../../Components/Products/Rims/Catalog/Collection";

import Pagination           from "../../../Components/Products/Rims/Catalog/Pagination";

import NoProductsFound      from "../../../Components/Products/Shared/Catalog/NoProductsFound";

import { gql } from "@apollo/client";

import { useQuery } from "@apollo/client";


const VISUAL_MARKUP_QUERY = gql`query {

    visualMarkup {

        markupPercent

    }

}`;

/**
 * 
 * @returns Rims product catalog page
 * 
 */

const RimsCatalog = () => { 
    
    const { data : visualMarkupData, loading : visualMarkupLoading, error } = useQuery ( VISUAL_MARKUP_QUERY );
    
    const 

    location                = useLocation (),

    { loading, data }       = Collection ( useParams ().parameters ),
    
    { t, i18n }             = useTranslation (),

    currentLanguage         = normalizeLanguage(i18n?.resolvedLanguage || i18n?.language),

    categoryPath            = buildCategoryPath ( "rim" ),

    hasNonLanguageQuery     = [ ...new URLSearchParams ( location.search ).keys () ].some ( key => key !== "lang" ),

    indexable               = location.pathname === categoryPath && ! hasNonLanguageQuery,

    canonicalHref           = localizedAbsoluteUrl ( categoryPath, currentLanguage ),

    alternateLinks          = hreflangLinks ( categoryPath ),

    robotsDirective         = indexable ? getRobotsDirective ( currentLanguage ) : "noindex,follow",

    pageDescription         = localizedValue({
        lv: "Disku katalogs Tireshop.lv - vieglmetāla un tērauda diski dažādiem auto modeļiem. Filtrē pēc izmēra, ET, skrūvju attāluma un cenas.",
        en: "Rim catalog at Tireshop.lv - alloy and steel rims for different car models. Filter by size, ET, bolt pattern, and price.",
        ru: "Каталог дисков Tireshop.lv - литые и стальные диски для разных моделей авто. Фильтруйте по размеру, ET, разболтовке и цене."
    }, currentLanguage),

    { layout, setLayout }   = Display (),

    isMounted                           = useRef ( false ),

    [ notification, setNotification ]   = useState ( 0 ),

    filters                 = useRef ( null ),

    [ 
        
        mobileFilters, 
        
        setMobileFilters ]  = useState ( false ),

    /** Product loader */

    ProductCatalog = () => { if ( loading || visualMarkupLoading ) return <ComponentLoader />

        if ( data === undefined || ! data.multiFieldProductSearch.edges.length ) return <NoProductsFound />

        return <Products layout={ layout } products={ data.multiFieldProductSearch.edges } notice={ { view: notification, set: setNotification } } visualMarkup={ visualMarkupData?.visualMarkup } />

    };

    useEffect ( () => {

        if (isMounted.current) {
            const timer = setTimeout(() => setNotification ( "" ), 1000);
            return () => clearTimeout(timer);
          } else {

            isMounted.current = true;
          }

    }, [ notification ] );

    usePosition ();

    /** Render component */

    return ( <div className="container max-content-width">

        <Helmet defer={ false }>

            <title>{ `Tireshop.lv | ${ t ( "rims" ) }` }</title>

            <meta
                name="description"
                content={ pageDescription }
            />

            <meta name="robots" content={ robotsDirective } />

            <link rel="canonical" href={ canonicalHref } />

            { alternateLinks.map((item) => (
                <link
                    key={ item.hreflang }
                    rel="alternate"
                    hrefLang={ item.hreflang }
                    href={ item.href }
                />
            )) }

        </Helmet>

        <Breadcrumbs collection={ [

            { name: t ( "rims" ), URI: categoryPath }

        ] } />

        <RimsLayout title={ t ( "rims" ) } filters={ { view: mobileFilters, set: setMobileFilters, element: filters } }>

            <Actions layout={ { view: layout, set: setLayout } } mobile={ { view: mobileFilters, set: setMobileFilters, element: filters } } />

            <ProductCatalog />

            { ! loading && 

                ( data !== undefined ) && 

                <Pagination

                    pageInfo={ data.multiFieldProductSearch.pageInfo }

                    totalCount={ data.multiFieldProductSearch.totalCount }

                />

            }

        </RimsLayout>
        
    </div> );

}

/** Export component */
 
export default RimsCatalog;
